import { createAsyncThunk } from '@reduxjs/toolkit';
import { ValidateIdentificationsResult } from '../types/api/documentValidation';
import { RootState } from '../reducers';

const urlPrefix = process.env.API_URL ?? '';

export const validateNricDocument = createAsyncThunk<
  ValidateIdentificationsResult,
  { base64Image: string; side?: 'front' | 'back' },
  { state: RootState; rejectValue: ValidateIdentificationsResult }
>(
  'documentValidation/validateNric',
  async ({ base64Image, side }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const refID = state.application.refId ?? '';

      // Fetch JWT token (same pattern as baseQueryWithAuth in base.ts)
      let tokenResponse: Response;
      try {
        tokenResponse = await fetch(`${urlPrefix}/token/generate-payload`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            payload: JSON.stringify(refID),
          }),
        });
      } catch {
        // API unavailable – skip validation
        return { status: 'ACCEPTED', message: '' };
      }

      if (!tokenResponse.ok) {
        // API unavailable – skip validation
        return { status: 'ACCEPTED', message: '' };
      }

      const tokenData = (await tokenResponse.json()) as { payload: string };
      const jwtToken = tokenData.payload;

      // Send validation request to .NET backend
      let response: Response;
      try {
        response = await fetch(
          `${urlPrefix}/documentvalidation/?relativeUrl=nric/validate`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              Authorization: `Bearer ${jwtToken}`,
            },
            body: JSON.stringify({
              refID,
              base64Image,
              ...(side && { side }),
            }),
          },
        );
      } catch {
        // API unavailable – skip validation
        return { status: 'ACCEPTED', message: '' };
      }

      if (!response.ok) {
        // API unavailable – skip validation
        return { status: 'ACCEPTED', message: '' };
      }

      const raw = await response.json();
      // card_side is nested inside ocr_result in the API response
      const rawCardSide: string | undefined =
        raw.ocr_result?.card_side ?? raw.card_side;
      const pass2Reasoning: string | undefined =
        raw.ocr_result?.pass2_reasoning;

      // If the OCR API behind the validation API is unavailable,
      // pass2_reasoning will contain the error - treat as accepted.
      if (
        pass2Reasoning &&
        pass2Reasoning.toLowerCase().includes('unavailable')
      ) {
        return { status: 'ACCEPTED', message: '' };
      }

      const result: ValidateIdentificationsResult = {
        status: raw.status,
        message: raw.message ?? '',
        rejection_reason: raw.rejection_reason ?? raw.rejectionReason,
        card_side: rawCardSide?.toLowerCase() as 'front' | 'back' | undefined,
        is_readable: raw.ocr_result?.is_readable ?? raw.is_readable,
        is_nric_document: raw.ocr_result?.is_nric_document ?? raw.is_nric_document,
        pass1_confidence: raw.ocr_result?.pass1_confidence,
        pass2_reasoning: pass2Reasoning,
      };
      return result;
    } catch {
      // Unexpected error (e.g. malformed response) - treat as service unavailable
      // and allow the upload to proceed rather than blocking the user.
      return { status: 'ACCEPTED', message: '' };
    }
  },
);
